/**
 * /market/:slug/gift-cards — Página pública de gift cards del storefront.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { ArrowLeft, Gift } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult, type MarketProduct } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { isGiftCardProduct } from "@/lib/gift-card-catalog";
import { getRequest } from "@tanstack/react-start/server";
import { marketSurfaceStyle } from "@/lib/market-theme";
import { StructuraPlatformSignature } from "@/components/StructuraPlatformSignature";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";
import { MarketProductCard } from "@/components/market/MarketProductCard";
import { storefrontPath } from "@/lib/tenant-host-paths";

type GiftCardsPageData = {
  catalog: MarketCatalogResult;
  giftCardProducts: MarketProduct[];
};

const fetchGiftCardsData = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<GiftCardsPageData> => {
    const request = getRequest();
    const catalog = await loadMarketCatalog(slug, { request });
    let giftCardProducts: MarketProduct[] = [];
    if (catalog.ok) {
      giftCardProducts = catalog.data.products.filter(isGiftCardProduct);
    }
    return { catalog, giftCardProducts };
  });

export const Route = createFileRoute("/market_/$slug/gift-cards")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchGiftCardsData({ data: params.slug }),
  component: GiftCardsPublicPage,
  head: ({ params }) => ({
    meta: [{ title: `Gift Cards — ${params.slug}` }],
    links: [
      { rel: "manifest", href: `/api/manifest/${params.slug}` },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
      },
    ],
  }),
});

function GiftCardsPublicPage() {
  const { slug } = Route.useParams();
  const { catalog, giftCardProducts } = Route.useLoaderData() as GiftCardsPageData;
  const navigate = useNavigate();

  const offlineScreen = renderMarketPublicOffline(catalog);
  if (offlineScreen) return offlineScreen;

  if (!catalog.ok) return null;
  const { tenant, config } = catalog.data;
  const brandStyle = catalog.data.brand.surfaceStyle;

  return (
    <div className="market-surface min-h-screen pb-16" style={brandStyle}>
      {/* Header simple */}
      <header className="sticky top-0 z-20 border-b border-[var(--market-surface-variant)] bg-[var(--market-surface)]/95 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-4xl items-center gap-3 px-4">
          <Link
            to="/market/$slug"
            params={{ slug }}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--market-on-surface-variant)] hover:bg-[var(--market-surface-variant)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <span className="market-font-display text-base font-bold text-[var(--market-on-surface)]">
            Gift Cards
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pt-8">
        {giftCardProducts.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <Gift className="h-12 w-12 text-[var(--market-on-surface-variant)] opacity-40" />
            <p className="text-[var(--market-on-surface-variant)]">
              No hay gift cards disponibles por el momento.
            </p>
            <Link
              to="/market/$slug"
              params={{ slug }}
              className="text-sm font-medium text-[var(--market-accent)] hover:underline"
            >
              Volver al inicio
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="market-font-display text-2xl font-bold text-[var(--market-on-surface)]">
                Gift Cards
              </h1>
              <p className="mt-1 text-sm text-[var(--market-on-surface-variant)]">
                Regalá una experiencia de compra en {tenant.name}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {giftCardProducts.map((product) => (
                <MarketProductCard
                  key={product.id}
                  product={product}
                  layout="grid"
                  qty={0}
                  requireDetail
                  detailHref={storefrontPath(slug, `/regalo/${product.slug}`)}
                  onAdd={() => {
                    void navigate({
                      to: "/market/$slug/regalo/$productSlug",
                      params: { slug, productSlug: product.slug },
                    });
                  }}
                  onIncrement={() => {}}
                  onDecrement={() => {}}
                />
              ))}
            </div>
          </>
        )}
      </main>

      <div className="mt-12 flex justify-center pb-4">
        <StructuraPlatformSignature />
      </div>
    </div>
  );
}
